import React from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import { iconFor } from '../lib/utils';
import type { PlayerAvatar } from '../types';

type CollectionsMenuProps = {
  onClose: () => void;
  discovered: string[];
  completedCollections: string[];
  playerAvatar: string;
  onSelectAvatar: (avatarKey: string) => void;
};

export const CollectionsMenu: React.FC<CollectionsMenuProps> = ({
  onClose,
  discovered,
  completedCollections,
  playerAvatar,
  onSelectAvatar,
}) => {
  const isAvatarUnlocked = (avatar: PlayerAvatar) => {
    if (!avatar.collectionRequirement) return true; // Default avatars are always unlocked
    return completedCollections.includes(avatar.collectionRequirement);
  };

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col p-8 w-[900px] max-w-[95vw] h-[80vh] rounded-xl"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
          boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 
            className="font-cinzel text-4xl font-bold"
            style={{ 
              color: CONSTANTS.ui.theme.colors.primary,
              textShadow: CONSTANTS.ui.theme.effects.textGlow
            }}
          >
            Collections & Avatars
          </h2>
          <button
            onClick={onClose}
            className="font-crimson px-6 py-2 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
              color: CONSTANTS.ui.theme.colors.background,
            }}
          >
            Close
          </button>
        </div>

        <div className="flex-grow grid grid-cols-1 md:grid-cols-3 gap-6 overflow-hidden">
          {/* Collections List */}
          <div className="md:col-span-2 overflow-y-auto pr-4 space-y-4">
            {CONSTANTS.collections.map(collection => {
              const discoveredCount = collection.elements.filter(el => discovered.includes(el)).length;
              const totalCount = collection.elements.length;
              const isComplete = discoveredCount === totalCount;

              return (
                <div key={collection.key} className="p-4 rounded-lg" style={{ background: CONSTANTS.ui.theme.colors.surface, border: `1px solid ${isComplete ? CONSTANTS.ui.theme.colors.success : CONSTANTS.ui.theme.colors.border}` }}>
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h3 className="font-cinzel text-xl font-bold" style={{ color: isComplete ? CONSTANTS.ui.theme.colors.success : CONSTANTS.ui.theme.colors.primary }}>{collection.name}</h3>
                      <p className="font-crimson text-sm" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>{collection.description}</p>
                    </div>
                    <div className="font-crimson text-sm px-3 py-1 rounded" style={{ background: CONSTANTS.ui.theme.colors.background, border: `1px solid ${CONSTANTS.ui.theme.colors.border}` }}>
                      {discoveredCount} / {totalCount}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                    {collection.elements.map(elKey => {
                      const isDiscovered = discovered.includes(elKey);
                      const element = isDiscovered ? CONSTANTS.elements[elKey] : null;
                      return (
                        <div key={elKey} title={element?.name || 'Undiscovered'} className="flex flex-col items-center text-center">
                          <div className="w-16 h-16 rounded-lg flex items-center justify-center mb-1" style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${CONSTANTS.ui.theme.colors.border}80`, opacity: isDiscovered ? 1 : 0.4 }}>
                            <img src={iconFor(isDiscovered ? elKey : 'default')} alt={element?.name || '?'} className="w-12 h-12 object-contain" />
                          </div>
                          <span className="font-crimson text-xs leading-tight" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>{element?.name || '???'}</span>
                        </div>
                      );
                    })}
                  </div>

                  {isComplete && (
                    <div className="mt-3 p-3 rounded" style={{ background: `rgba(34, 139, 34, 0.1)`, border: `1px solid ${CONSTANTS.ui.theme.colors.success}80` }}>
                      <h4 className="font-cinzel text-md font-bold" style={{ color: CONSTANTS.ui.theme.colors.success }}>Lore Unlocked:</h4>
                      <p className="font-crimson text-sm italic" style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}>{collection.reward.lore}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Avatars List */}
          <div className="overflow-y-auto pr-4 space-y-3">
            <h3 className="font-cinzel text-2xl font-bold" style={{ color: CONSTANTS.ui.theme.colors.primary }}>Avatars</h3>
            {CONSTANTS.player.avatars.map(avatar => {
              const unlocked = isAvatarUnlocked(avatar);
              return (
                <button 
                  key={avatar.key} 
                  onClick={() => unlocked && onSelectAvatar(avatar.key)}
                  disabled={!unlocked}
                  className="w-full flex items-center gap-4 p-3 rounded-lg transition-all duration-200 disabled:opacity-50"
                  style={{ 
                    background: playerAvatar === avatar.key ? CONSTANTS.ui.theme.colors.primary : CONSTANTS.ui.theme.colors.surface,
                    color: playerAvatar === avatar.key ? CONSTANTS.ui.theme.colors.background : CONSTANTS.ui.theme.colors.text,
                    border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
                    cursor: unlocked ? 'pointer' : 'not-allowed'
                  }}
                >
                  <img src={iconFor(avatar.key)} alt={avatar.name} className="w-12 h-12 rounded-full bg-black/30" />
                  <div className="text-left">
                    <div className="font-cinzel font-bold">{avatar.name}</div>
                    <div className="font-crimson text-xs" style={{ color: playerAvatar === avatar.key ? 'rgba(0,0,0,0.7)' : CONSTANTS.ui.theme.colors.textSecondary }}>
                      {unlocked ? 'Available' : `Unlock: ${CONSTANTS.collections.find(c => c.key === avatar.collectionRequirement)?.name || '???'}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
